import type { CreateVehicleTO } from '@control-center/control-center-client-public-vehicles-ts';
import type { VehicleSummary } from 'domain/vehicle';
import { formatDate, formatTime, getDateFromString } from 'helper/time';

export const getVehicleTypeName = (vehicle: VehicleSummary): string => {
    if (vehicle.type === 'SIMULATOR') return 'Simulated (agent)';
    if (vehicle.type === 'DUMMY') return 'End to end test';

    if (vehicle.type === 'TRATON') {
        switch (vehicle.subtype) {
            case 'PHYSICAL_VEHICLE':
                return 'Physical';
            case 'SIMULATED_VEHICLE_WITH_CONNECTIVITY':
                return 'Simulated (connectivity)';
            case 'HW_RIG':
                return 'HW rig';
        }
    }

    return 'Unknown';
};

// Frontend vehicle kinds selectable when creating a vehicle.
export type VehicleKind = 'PHYSICAL' | 'SIMULATOR' | 'HW_RIG' | 'SIMULATED_CONNECTIVITY';

// Create maps the opposite direction of getVehicleTypeName: a frontend
// selection is translated into the backend type/subtype pair.
export const getVehicleTypeIdentifiers = (kind: VehicleKind): Pick<CreateVehicleTO, 'type' | 'subtype'> => {
    switch (kind) {
        case 'PHYSICAL':
            return { type: 'TRATON', subtype: 'PHYSICAL_VEHICLE' };
        case 'SIMULATOR':
            return { type: 'SIMULATOR', subtype: 'UNKNOWN' };
        case 'HW_RIG':
            return { type: 'TRATON', subtype: 'HW_RIG' };
        case 'SIMULATED_CONNECTIVITY':
            return { type: 'TRATON', subtype: 'SIMULATED_VEHICLE_WITH_CONNECTIVITY' };
    }
};

export const isVehicleStale = (vehicle: VehicleSummary, days = 30): boolean => {
    const threshold = Date.now() - days * 24 * 60 * 60 * 1000;

    const vehicleConnectivity = vehicle.connectivity?.value;

    if (vehicleConnectivity?.value?.state === 'ONLINE') return false;

    const lastConnectedTime = new Date(vehicleConnectivity?.updatedAt ?? '').getTime();
    if (!Number.isNaN(lastConnectedTime)) return lastConnectedTime <= threshold;

    const createdTime = new Date(vehicle.createdAt ?? '').getTime();
    if (!Number.isNaN(createdTime) && createdTime > threshold) return false;

    return true;
};

export const formatDateTimeWithDash = (date?: string) => {
    const parsed = getDateFromString(date);
    return parsed ? `${formatTime(parsed)} - ${formatDate(parsed)}` : '';
};
